import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { clipsInSections, RECUT_CHIPS, type RecutChip } from 'fym-shared/src/music24/musicVideoDoc';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { newDocId } from '../doc/clipIds';
import { updateClip } from '../doc/clipOps';
import { copySectionCutToChoruses, isChorus } from '../doc/sectionOps';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { timelineMessages } from '../messages/timelineMessages';
import { RecutChips } from '../timeline/RecutChips';
import { recutErrorText } from '../timeline/recutErrorText';
import { formatTime } from '../timeline/timelineGeometry';
import { useRecut } from '../timeline/useRecut';
import { PhoneClipPicker } from './PhoneClipPicker';
import { SheetClipStrip } from './SheetClipStrip';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The sheet of one section on a phone (design f2): its clips, "Swap this clip" (the footage picker),
 * "Kept on recut", the recut chips and "Recut <section> · no credits", "Use this cut for every chorus".
 */
const SectionSheetComponent: React.FC<{ sectionIndex: number; onClose: () => void }> = ({ sectionIndex, onClose }) => {
  const intl = useIntl();
  const { doc, songMap, change, readOnly } = useMusicVideoEditor();
  const section = songMap.sections.find((item) => item.index === sectionIndex) ?? null;
  const clips = useMemo(
    () => clipsInSections(doc, songMap.sections, [sectionIndex]),
    [doc, sectionIndex, songMap.sections]
  );
  const [clipId, setClipId] = useState<string | null>(clips[0]?.id ?? null);
  const [isPicking, setIsPicking] = useState(false);
  const [chips, setChips] = useState<RecutChip[]>([]);
  const { recut, isBusy, error, saveError } = useRecut();
  const errorText = recutErrorText(intl, error, saveError);
  const clip = clips.find((item) => item.id === clipId) ?? null;
  if (section === null) {
    return null;
  }
  return (
    <>
      <button
        type="button"
        className={styles.sheetBackdrop}
        aria-label={intl.formatMessage(panelMessages.phoneClose)}
        onClick={onClose}
      />
      <div className={styles.sheet} role="dialog" aria-label={section.label}>
        <span className={styles.sheetGrab} />
        <h4>
          {section.label}
          <span className={styles.grow} />
          <small className={styles.hint}>
            {intl.formatMessage(timelineMessages.recutFacts, {
              from: formatTime(section.startSec),
              to: formatTime(section.endSec),
              count: clips.length,
            })}
          </small>
        </h4>
        {isPicking && clip !== null ? (
          <PhoneClipPicker section={section} clipId={clip.id} onDone={() => setIsPicking(false)} />
        ) : (
          <>
            <SheetClipStrip clips={clips} selectedId={clipId} onSelect={setClipId} />
            <div className={styles.row}>
              <button
                type="button"
                className={styles.ghostButton}
                disabled={readOnly || clip === null}
                onClick={() => setIsPicking(true)}
              >
                {intl.formatMessage(panelMessages.phoneSwap)}
              </button>
              <button
                type="button"
                className={clsx(styles.ghostButton, clip?.keep === true && styles.ghostButtonOn)}
                disabled={readOnly || clip === null}
                onClick={() => clip !== null && change((current) => updateClip(current, clip.id, { keep: !clip.keep }))}
              >
                {intl.formatMessage(clip?.keep === true ? panelMessages.kept : panelMessages.keep)}
              </button>
            </div>
            <div className={styles.separator} />
            <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.phoneRecut)}</span>
            <RecutChips chips={RECUT_CHIPS} picked={chips} onChange={setChips} />
            <SMLButton
              omitCap
              stretch
              isLoading={isBusy}
              disabled={readOnly || isBusy}
              onClick={() => {
                recut([section.index], chips, '')
                  .then((started) => started && onClose())
                  .catch(console.error);
              }}
            >
              {intl.formatMessage(timelineMessages.recutButton, { section: section.label })}
            </SMLButton>
            {errorText !== null && (
              <p className={styles.errorText} role="alert">
                {errorText}
              </p>
            )}
            {isChorus(section) && (
              <button
                type="button"
                className={styles.ghostButton}
                disabled={readOnly}
                onClick={() => {
                  change((current) =>
                    copySectionCutToChoruses(current, songMap.sections, section.index, () => newDocId())
                  );
                  onClose();
                }}
              >
                {intl.formatMessage(timelineMessages.everyChorus)}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};

export const SectionSheet = TypedMemo(SectionSheetComponent);
